import React, {useEffect, useState} from "react";
import {
    Alert,
    Breadcrumb,
    Button,
    Form,
    Input,
    Layout,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    message,
} from "antd";
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    CrownOutlined, FireOutlined, HeartOutlined,
    PlusOutlined,
    StarOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
} from "@ant-design/icons";
import {Link} from "react-router-dom";
import Api from "../api";

const {Content} = Layout;
const {Option} = Select;

const ICON_OPTIONS = [
    {value: "crown", label: "Crown", Icon: CrownOutlined},
    {value: "star", label: "Star", Icon: StarOutlined},
    {value: "trophy", label: "Trophy", Icon: TrophyOutlined},
    {value: "thunderbolt", label: "Thunderbolt", Icon: ThunderboltOutlined},
    {value: "heart", label: "Heart", Icon: HeartOutlined},
    {value: "fire", label: "Fire", Icon: FireOutlined},
];

const STYLE_OPTIONS = [
    {value: "gold", color: "gold"},
    {value: "purple", color: "purple"},
    {value: "blue", color: "blue"},
    {value: "green", color: "green"},
    {value: "pink", color: "pink"},
];

const PLATFORM_OPTIONS = [
    {value: "both", label: "Both"},
    {value: "mobile", label: "Mobile"},
    {value: "desktop", label: "Desktop"},
];

const ICON_MAP = Object.fromEntries(ICON_OPTIONS.map(({value, Icon}) => [value, Icon]));

const renderIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || TrophyOutlined;

    return <IconComponent style={{fontSize: 18}}/>;
};

const renderStyleTag = (style) => {
    const match = STYLE_OPTIONS.find((option) => option.value === style);
    const color = match?.color || STYLE_OPTIONS[0].color;
    const label = match?.value || "gold";

    return <Tag color={color}>{label}</Tag>;
};

const isLinkActive = (record) => !!(record?.is_active ?? record?.active ?? record?.enabled);

const MenuLinksView = () => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [form] = Form.useForm();

    const fetchLinks = () => {
        setLoading(true);

        Api.get("/admin/menu-links")
            .then((res) => {
                const items = res.data?.data || [];
                setLinks([...items].sort((a, b) => a.order - b.order));
            })
            .catch(() => message.error("Failed to load menu links"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchLinks();
    }, []);

    const openCreateModal = () => {
        setEditingLink(null);
        form.resetFields();
        form.setFieldsValue({
            icon: "trophy",
            style: "gold",
            platform: "both",
        });
        setOpenModal(true);
    };

    const openEditModal = (record) => {
        setEditingLink(record);
        form.setFieldsValue({
            title: record.title,
            url: record.url,
            icon: record.icon || "trophy",
            style: STYLE_OPTIONS.some((option) => option.value === record.style) ? record.style : "gold",
            platform: record.platform || "both",
        });
        setOpenModal(true);
    };

    const handleSave = () => {
        form.validateFields().then((values) => {
            const payload = {
                title: values.title.trim(),
                url: values.url.trim(),
                icon: values.icon || null,
                style: values.style,
                platform: values.platform,
            };

            const request = editingLink
                ? Api.put(`/admin/menu-links/${editingLink.id}`, payload)
                : Api.post("/admin/menu-links", payload);

            request
                .then(() => {
                    message.success(editingLink ? "Menu link updated" : "Menu link added");
                    setOpenModal(false);
                    setEditingLink(null);
                    form.resetFields();
                    fetchLinks();
                })
                .catch(() => message.error(editingLink ? "Update failed" : "Add failed"));
        });
    };

    const handleDelete = (id) => {
        Api.delete(`/admin/menu-links/${id}`)
            .then(() => {
                message.success("Menu link deleted");
                fetchLinks();
            })
            .catch(() => message.error("Delete failed"));
    };

    const handleToggle = (record) => {
        Api.post(`/admin/menu-links/${record.id}/toggle`)
            .then(() => {
                message.success("Status updated");
                fetchLinks();
            })
            .catch(() => message.error("Failed to update status"));
    };

    const reorder = (row, direction) => {
        const sorted = [...links].sort((a, b) => a.order - b.order);
        const index = sorted.findIndex((item) => item.id === row.id);
        const swapIndex = direction === "up" ? index - 1 : index + 1;

        if (swapIndex < 0 || swapIndex >= sorted.length) {
            return;
        }

        const reordered = [...sorted];
        [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

        const payload = reordered.map((item, order) => ({
            id: item.id,
            order,
        }));

        Api.post("/admin/menu-links/reorder", {links: payload})
            .then(() => {
                message.success("Order updated");
                fetchLinks();
            })
            .catch(() => message.error("Reorder failed"));
    };

    const columns = [
        {
            title: "Order",
            dataIndex: "order",
            width: 80,
        },
        {
            title: "Title",
            dataIndex: "title",
        },
        {
            title: "URL",
            dataIndex: "url",
            ellipsis: true,
        },
        {
            title: "Icon",
            dataIndex: "icon",
            width: 90,
            render: (value) => renderIcon(value),
        },
        {
            title: "Style",
            dataIndex: "style",
            width: 100,
            render: (value) => renderStyleTag(value),
        },
        {
            title: "Platform",
            dataIndex: "platform",
            width: 110,
            render: (value) => <Tag>{value || "both"}</Tag>,
        },
        {
            title: "Active",
            dataIndex: "is_active",
            width: 90,
            render: (_, record) => (
                <Switch
                    checked={isLinkActive(record)}
                    onChange={() => handleToggle(record)}
                />
            ),
        },
        {
            title: "Actions",
            width: 220,
            render: (_, record) => (
                <Space wrap>
                    <Button size="small" icon={<ArrowUpOutlined/>} onClick={() => reorder(record, "up")}/>
                    <Button size="small" icon={<ArrowDownOutlined/>} onClick={() => reorder(record, "down")}/>
                    <Button type="link" onClick={() => openEditModal(record)}>Edit</Button>
                    <Popconfirm title="Delete this menu link?" onConfirm={() => handleDelete(record.id)}>
                        <Button type="link" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Content style={{margin: "0 16px"}}>
            <Breadcrumb style={{margin: "16px 0"}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item><Link to="/menu-links">Menu links</Link></Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Space style={{marginBottom: 16}}>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={openCreateModal}>
                        Add menu link
                    </Button>
                </Space>

                <Table
                    size="small"
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={links}
                    pagination={false}
                />
            </div>

            <Modal
                title={editingLink ? "Edit menu link" : "Add menu link"}
                open={openModal}
                onOk={handleSave}
                onCancel={() => {
                    setOpenModal(false);
                    setEditingLink(null);
                    form.resetFields();
                }}
                okText="Save"
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{required: true, message: "Title is required"}]}
                    >
                        <Input placeholder="Promo title"/>
                    </Form.Item>

                    <Form.Item
                        name="url"
                        label="URL"
                        rules={[{required: true, message: "URL is required"}]}
                    >
                        <Input placeholder="/settings or https://..."/>
                    </Form.Item>

                    <Form.Item name="icon" label="Icon">
                        <Select allowClear placeholder="Default: trophy">
                            {ICON_OPTIONS.map(({value, label, Icon}) => (
                                <Option key={value} value={value}>
                                    <Space>
                                        <Icon/>
                                        {label}
                                    </Space>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="style" label="Style">
                        <Select>
                            {STYLE_OPTIONS.map(({value, color}) => (
                                <Option key={value} value={value}>
                                    <Tag color={color}>{value}</Tag>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="platform" label="Platform">
                        <Select>
                            {PLATFORM_OPTIONS.map(({value, label}) => (
                                <Option key={value} value={value}>{label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

export default MenuLinksView;
